import './utils/Date';
import { createApp, defineAsyncComponent } from 'vue';
import 'vue-next-select/dist/index.css';
import '@/interfaces/WindowInterface';
import BaseGridRow from '@/components/BaseGridRow.vue';
import BaseGridCol from '@/components/BaseGridCol.vue';
import BaseGridWrap from '@/components/BaseGridWrap.vue';
import BasePageHeader from '@/components/BasePageHeader.vue';
import IoRouterLink from '@/components/IoRouterLink.vue';
import BaseLoader from '@/components/BaseLoader.vue';
import BaseButton from '@/components/BaseButton.vue';
import BaseSpacer from '@/components/BaseSpacer.vue';
import BaseIconButton from '@/components/BaseIconButton.vue';
import BaseEqualizer from '@/components/BaseEqualizer.vue';
import BaseEqualizerElement from '@/components/BaseEqualizerElement.vue';
import BaseEqualizerElementPart from '@/components/BaseEqualizerElementPart.vue';
import IoHeadline from '@/components/Headline.vue';
import IoHero from '@/components/Hero.vue';
import Equalize from '@/utils/Equalize';
import App from './App.vue';
import router from './router';
import store from './store';

const PageError = defineAsyncComponent({
  loader: () => import(/* webpackChunkName: "chunks/error-pages" */ '@/components/PageError.vue'),
  loadingComponent: BaseLoader,
});

const IoSection = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/text-sections" */ '@/components/Section.vue'));
const CompanyCertificates = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/text-sections" */ '@/components/CompanyCertificates.vue'));
const IoContainer = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/text-sections" */ '@/components/Container.vue'));
const IoText = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/text-sections" */ '@/components/Text.vue'));
const IoImageSpacer = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/text-sections" */ '@/components/ImageSpacer.vue'));
const IoCard = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/text-sections" */ '@/components/Card.vue'));
const IoMediaCard = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/text-sections" */ '@/components/MediaCard.vue'));
const MediaSection = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/text-sections" */ '@/components/MediaSection.vue'));
const IoFaqs = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/text-sections" */ '@/components/Faqs.vue'));
const IoList = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/text-sections" */ '@/components/List.vue'));
const IoTooltip = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/text-sections" */ '@/components/Tooltip.vue'));
const IoCustomerFeedback = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/text-sections" */ '@/components/CustomerFeedback.vue'));
const PowerLabelings = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/charts" */ '@/components/PowerLabelings.vue'));
const IoResponsiveImage = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/text-sections" */ '@/components/ResponsiveImage.vue'));
const BaseRating = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/text-sections" */ '@/components/BaseRating.vue'));
const BaseTabs = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/text-sections" */ '@/components/BaseTabs.vue'));
const BaseTabsTab = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/text-sections" */ '@/components/BaseTabsTab.vue'));

const HeroSlider = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/hero-slider" */ '@/components/HeroSlider.vue'));
const HeroSlide = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/hero-slider" */ '@/components/HeroSlide.vue'));

const BaseCalculator = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/calculator" */ '@/components/BaseCalculator.vue'));

const StepAddress = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/order-pages" */ '@/components/order/StepAddress.vue'));
const StepConnection = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/order-pages" */ '@/components/order/StepConnection.vue'));
const StepPayment = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/order-pages" */ '@/components/order/StepPayment.vue'));
const StepOverview = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/order-pages" */ '@/components/order/StepOverview.vue'));
const InputSection = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/order-pages" */ '@/components/order/InputSection.vue'));

const IoCardContact = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/contact-forms" */ '@/components/CardContact.vue'));
const IoSectionContact = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/text-sections" */ '@/components/SectionContact.vue'));
const IoFormContactCallback = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/contact-forms" */ '@/components/contact/CallbackForm.vue'));
const IoFormContactEmail = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/contact-forms" */ '@/components/contact/EmailForm.vue'));

const IconAndroid = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconAndroid.vue'));
const IconTree = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconTree.vue'));
const IconApple = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconApple.vue'));
const IconCallback = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconCallback.vue'));
const IconFacebook = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconFacebook.vue'));
const IconInstagram = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconInstagram.vue'));
const IconLiveChat = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconLiveChat.vue'));
const IconTwitter = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconTwitter.vue'));
const IconVideoConsulting = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconVideoConsulting.vue'));
const IconWhatsapp = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconWhatsapp.vue'));
const IconYoutube = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconYoutube.vue'));
const IconContact = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconContact.vue'));
const IconServiceApp = defineAsyncComponent(() => import(/* webpackChunkName: "chunks/icons" */ '@/components/icons/IconServiceApp.vue'));

const BaseCalculatorV2 = defineAsyncComponent(() => import((/* webpackChunkName: "chunks/calculatorV2" */ '@/components/BaseCalculatorV2.vue')));
const AdventCalender = defineAsyncComponent(() => import(/* webpackChunkName: 'chunks/advent-calender' */ '@/AdventCalender.vue'));

window.runIoEqualize = () => {
  Equalize.run();
};

const ioApp = createApp(App)
  .component('BaseGridRow', BaseGridRow)
  .component('BaseGridCol', BaseGridCol)
  .component('BaseGridWrap', BaseGridWrap)
  .component('BaseLoader', BaseLoader)
  .component('IoHeadline', IoHeadline)
  .component('BasePageHeader', BasePageHeader)
  .component('IoHero', IoHero)
  .component('IoRouterLink', IoRouterLink)

  .component('IoSection', IoSection)
  .component('CompanyCertificates', CompanyCertificates)
  .component('IoContainer', IoContainer)
  .component('IoText', IoText)
  .component('BaseButton', BaseButton)
  .component('BaseIconButton', BaseIconButton)
  .component('BaseSpacer', BaseSpacer)
  .component('IoImageSpacer', IoImageSpacer)
  .component('IoCard', IoCard)
  .component('IoList', IoList)
  .component('IoTooltip', IoTooltip)
  .component('IoMediaCard', IoMediaCard)
  .component('IoFaqs', IoFaqs)
  .component('IoCustomerFeedback', IoCustomerFeedback)
  .component('PowerLabelings', PowerLabelings)
  .component('IoResponsiveImage', IoResponsiveImage)
  .component('MediaSection', MediaSection)
  .component('BaseRating', BaseRating)
  .component('BaseEqualizer', BaseEqualizer)
  .component('BaseEqualizerElement', BaseEqualizerElement)
  .component('BaseEqualizerElementPart', BaseEqualizerElementPart)
  .component('BaseTabs', BaseTabs)
  .component('BaseTabsTab', BaseTabsTab)

  .component('HeroSlider', HeroSlider)
  .component('HeroSlide', HeroSlide)

  .component('BaseCalculator', BaseCalculator)

  .component('StepAddress', StepAddress)
  .component('StepConnection', StepConnection)
  .component('StepPayment', StepPayment)
  .component('StepOverview', StepOverview)
  .component('InputSection', InputSection)

  .component('IoCardContact', IoCardContact)
  .component('IoSectionContact', IoSectionContact)
  .component('IoFormContactCallback', IoFormContactCallback)
  .component('IoFormContactEmail', IoFormContactEmail)

  .component('IconAndroid', IconAndroid)
  .component('IconApple', IconApple)
  .component('IconCallback', IconCallback)
  .component('IconFacebook', IconFacebook)
  .component('IconInstagram', IconInstagram)
  .component('IconLiveChat', IconLiveChat)
  .component('IconTwitter', IconTwitter)
  .component('IconVideoConsulting', IconVideoConsulting)
  .component('IconWhatsapp', IconWhatsapp)
  .component('IconYoutube', IconYoutube)
  .component('IconTree', IconTree)
  .component('IconContact', IconContact)
  .component('IconServiceApp', IconServiceApp)

  .component('BaseCalculatorV2', BaseCalculatorV2)
  .component('AdventCalender', AdventCalender)

  .component('PageError', PageError)

  .use(store)
  .use(router);

ioApp.mount('#ioapp');
